import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'utils/toast';
import { API_REQUEST_TIMEOUT_MS } from 'constants/appConstants';
import { REFRESH_TOKEN_API_URL } from 'constants/apiConstants';
import {
  ACCESS_TOKEN_STRING,
  AUDIENCE_ID,
  REFRESH_TOKEN_STRING,
} from 'constants/authConstants';
import { CHECK_EMAIL_PATH } from 'constants/routeConstants';
import {
  CONFLICT_API_ERROR_MESSAGE,
  CONFLICT_RESOURCE_API_ERROR_MESSAGE,
  FORBIDDEN_API_ERROR_MESSAGE,
  FORBIDDEN_RESOURCE_API_ERROR_MESSAGE,
  INVALID_REQUEST_API_ERROR_MESSAGE,
  INVALID_REQUEST_RESOURCE_API_ERROR_MESSAGE,
  LICENCE_EXPIRED_API_ERROR_MESSAGE,
  NOT_FOUND_API_ERROR_MESSAGE,
  NOT_FOUND_RESOURCE_API_ERROR_MESSAGE,
} from 'constants/messageConstants';

// ── Resource name extraction ──────────────────────────────────────────────────
// Strips leading consecutive-uppercase prefixes from API URL segments, then
// inserts spaces at PascalCase word boundaries.
// e.g. "GChemical"   → "Chemical"
//      "GSeedSpecies" → "Seed Species"
//      "Input"        → "Input"
const formalizeResource = (raw: string): string => {
  let i = 0;
  while (i + 1 < raw.length && /[A-Z]/.test(raw[i + 1])) {
    i++;
  }
  const stripped = i > 0 ? raw.slice(i) : raw;
  return stripped.replace(/([a-z])([A-Z])/g, '$1 $2');
};

// Extracts and formalizes the resource segment from an API URL.
// "/api/Input/GetByOrgActive/..."   → "Input"
// "/api/GChemical/Browse/..."       → "Chemical"
// "/api/GSeedSpecies/GetById/..."   → "Seed Species"
// Returns undefined when the URL does not match the expected pattern.
const extractResource = (url: string | undefined): string | undefined => {
  const raw = url?.match(/\/api\/([^/]+)/i)?.[1];
  return raw ? formalizeResource(raw) : undefined;
};

// ── Per-request flag extensions ───────────────────────────────────────────────
// _silentErrors: suppress toast for 400 / 403 / 404 / 409 / 410 / 422. Set on
//   initial workspace load APIs so a cold-cache miss doesn't surface as a
//   user-visible error, and on calls that report failures inline (M writes).
// _retry: prevents the 401-refresh handler re-entering on the replayed request.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _silentErrors?: boolean;
    _retry?: boolean;
  }
  interface AxiosRequestConfig {
    _silentErrors?: boolean;
  }
}

// ── Retryable error classification (OP-2102) ──────────────────────────────────
// Non-retryable: specific 4xx codes that indicate a permanent client-side issue.
// Retryable: network failures, 408 (request timeout), 429 (rate limit), 5xx.
const NON_RETRYABLE_STATUSES = new Set([400, 401, 402, 403, 404, 409, 410, 422]);

/**
 * Returns true when the error is safe to retry.
 * Passed directly to the React Query global `retry` function in index.tsx.
 * When the browser is offline, retrying immediately is pointless — the offline
 * banner already tells the user to check their connection.
 */
export const isRetryableError = (error: unknown): boolean => {
  if (!navigator.onLine) return false; // offline — skip retries
  const status = (error as AxiosError)?.response?.status;
  if (!status) return true; // network error / axios timeout — retryable
  return !NON_RETRYABLE_STATUSES.has(status);
};

// ── Shared logout (no React context available in interceptors) ────────────────
const performLogout = () => {
  Cookies.remove(ACCESS_TOKEN_STRING);
  Cookies.remove(REFRESH_TOKEN_STRING);
  // Brief pause so any visible toast renders before the page navigates away.
  setTimeout(() => {
    window.location.href = CHECK_EMAIL_PATH;
  }, 2000);
};

// ── Axios instance ────────────────────────────────────────────────────────────
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: API_REQUEST_TIMEOUT_MS,
});

axiosInstance.interceptors.request.use((config) => {
  // Fail fast when offline — avoids waiting for API_REQUEST_TIMEOUT_MS.
  // The response interceptor is not triggered for request errors, so no toast
  // is fired here; the global OfflineBanner handles user communication.
  if (!navigator.onLine) {
    return Promise.reject(new AxiosError('No network connection', 'ERR_NETWORK'));
  }
  const token = Cookies.get(ACCESS_TOKEN_STRING);
  config.headers['Authorization'] = token ? `Bearer ${token}` : undefined;
  return config;
}, Promise.reject);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig;
    const status = error.response?.status;

    // ── 401 Unauthorised — attempt JWT refresh, then fall back to logout ──────
    if (status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      const refreshToken = Cookies.get(REFRESH_TOKEN_STRING);
      if (refreshToken && AUDIENCE_ID) {
        try {
          const formData = new FormData();
          formData.append('Client_Id', AUDIENCE_ID);
          formData.append('Refresh_token', refreshToken);
          formData.append('Grant_type', 'refresh_token');

          // Plain axios (not axiosInstance) to avoid re-entering this interceptor.
          const { data } = await axios.post<{
            access_token: string;
            refresh_token?: string;
          }>(`${process.env.REACT_APP_BASE_URL}${REFRESH_TOKEN_API_URL}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          if (data.access_token) {
            Cookies.set(ACCESS_TOKEN_STRING, data.access_token, { expires: 1 });
            if (data.refresh_token) {
              Cookies.set(REFRESH_TOKEN_STRING, data.refresh_token, {
                expires: 7,
              });
            }
            originalRequest.headers['Authorization'] =
              `Bearer ${data.access_token}`;
            return axiosInstance(originalRequest);
          }
        } catch {
          // Refresh failed — fall through to logout below.
        }
      }

      performLogout();
      return Promise.reject(error);
    }

    // ── 402 Licence expired ───────────────────────────────────────────────────
    if (status === 402) {
      toast.error(LICENCE_EXPIRED_API_ERROR_MESSAGE);
      performLogout();
      return Promise.reject(error);
    }

    // ── 400 / 403 / 409 — notify unless the caller shows its own inline error ─
    const resource = extractResource(originalRequest?.url);

    if (!originalRequest?._silentErrors) {
      if (status === 400) {
        toast.error(
          resource
            ? INVALID_REQUEST_RESOURCE_API_ERROR_MESSAGE(resource)
            : INVALID_REQUEST_API_ERROR_MESSAGE
        );
      } else if (status === 403) {
        toast.error(
          resource
            ? FORBIDDEN_RESOURCE_API_ERROR_MESSAGE(resource)
            : FORBIDDEN_API_ERROR_MESSAGE
        );
      } else if (status === 409) {
        toast.error(
          resource
            ? CONFLICT_RESOURCE_API_ERROR_MESSAGE(resource)
            : CONFLICT_API_ERROR_MESSAGE
        );
      }
    }

    // ── 404 / 410 / 422 — silent for initial load, notify otherwise ──────────
    if (
      (status === 404 || status === 410 || status === 422) &&
      !originalRequest?._silentErrors
    ) {
      toast.error(
        resource
          ? NOT_FOUND_RESOURCE_API_ERROR_MESSAGE(resource)
          : NOT_FOUND_API_ERROR_MESSAGE
      );
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
